import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import SimpleBar from "simplebar-react";
import {
  PlayerTv,
  TvChannelsList,
  TvModelChannelsToggle,
  TvConductorList,
  TvConductorListMobile,
  PlayerOverlay,
} from "src/components";
import { SeoService, UIService, _ } from "src/services";
import { TvModel, TvAction } from "src/store";
import logo from "src/assets/static/img/main/anten-logo-new-light.svg";
import { ProgramAvailablity, ProgramType } from "src/enums";

export const Tv: React.FC = () => {
  //#region dependencies

  const { slug }: any = useParams();
  const { push } = useHistory();
  const dispatch = useDispatch();
  const conductor: TvModel = useSelector((state: any) => state.TvState);

  //#endregion

  //#region properties

  const mainRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const tvRef = useRef<HTMLDivElement>(null);
  const conductorWrapperRef = useRef<HTMLDivElement>(null);
  const conductorListRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<typeof SimpleBar>(null);

  //#endregion

  //#region effects

  useEffect(() => {
    if (!slug && conductor.channels.length) {
      const ch3 =
        conductor.channels.find((x) => x.id === 3) || conductor.channels[0];
      if (ch3) push(`/tv/${ch3.slug}`);
    } else {
      const current = conductor.channels.find((x) => x.slug === slug);
      if (current?.id !== conductor.currentChannel?.id) {
        dispatch(TvAction.setCurrentChannel(current));
        SeoService.setHeaderMetadata(_("liveBroadcast") + " " + current?.title);
      }
    }
  }, [slug, push, conductor, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(TvAction.setCurrentChannel(undefined));
      dispatch(TvAction.setStreamUrl(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    const handleKeepViewMode = () => {
      UIService.handleKeepViewMode(mainRef, wrapperRef, contentRef, titleRef);
      UIService.handleKeepTvEqualHeight(
        tvRef,
        conductorWrapperRef,
        conductorListRef
      );
    };

    window.addEventListener("resize", handleKeepViewMode, false);
    handleKeepViewMode();

    return () => {
      window.removeEventListener("resize", handleKeepViewMode, false);
    };
  }, []);

  useEffect(() => {
    if (conductor.currentChannel) {
      dispatch(TvAction.getStreamUrl(conductor.currentChannel.id));
    }
  }, [conductor.currentChannel, dispatch]);

  //#endregion

  return (
    <div className="live-tv-page app-theme2" ref={mainRef}>
      <div>
        <div className="live-tv-page--header" ref={wrapperRef}>
          <div className="container text-white" ref={contentRef}>
            <h1
              className="text-center live-tv-page--title text-rtl"
              ref={titleRef}
            >
              {conductor.currentChannel?.title}
              <span>({_("LIVE")})</span>
            </h1>
            <div className="row">
              <div className="col-12 col-lg-9 no-padding-mobile">
                <div
                  className="media-player-area--ratio-fixer"
                  style={{ backgroundColor: "#000" }}
                >
                  <div className="media-player-area big-mirror" ref={tvRef}>
                    {conductor.currentChannel && (
                      <div className="vjs-player">
                        <div
                          style={{
                            backgroundImage: `url(${conductor.currentChannel?.thumbnail})`,
                          }}
                          className="no-video-cover"
                        />
                      </div>
                    )}
                    {conductor.availability !==
                      ProgramAvailablity.Available && (
                      <PlayerOverlay
                        programType={ProgramType.Tv}
                        streamId={conductor.episode?.id || 0}
                        channelId={conductor.currentChannel?.id}
                        otherSession={conductor.killSessionId}
                        {...conductor.message}
                        availability={
                          conductor.availability ||
                          ProgramAvailablity.Simultaneous
                        }
                      />
                    )}
                    {conductor.player &&
                    conductor.availability === ProgramAvailablity.Available ? (
                      <PlayerTv {...conductor.player} />
                    ) : (
                      <div className="shimmer absolute">
                        <img src={logo} alt="" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="col-12 col-lg-3 programs-list d-none d-lg-block"
                ref={conductorWrapperRef}
              >
                <TvConductorList
                  listRef={conductorListRef}
                  scrollRef={scrollRef}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-12 p-0">
              <TvModelChannelsToggle />
              <TvConductorListMobile />
              <TvChannelsList />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
